import { createContext, useContext, useState, useEffect, useCallback } from "react";
import axios from "axios";
import { API } from "../utils/api";

const PlatformStatusContext = createContext();

export const usePlatformStatus = () => {
  const context = useContext(PlatformStatusContext);
  if (!context) {
    throw new Error("usePlatformStatus must be used within a PlatformStatusProvider");
  }
  return context;
};

export const PlatformStatusProvider = ({ children }) => {
  const [isAppEnabled, setIsAppEnabled] = useState(true);
  const [isMaintenanceMode, setIsMaintenanceMode] = useState(false);
  const [maintenanceMessage, setMaintenanceMessage] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [lastChecked, setLastChecked] = useState(null);

  const checkPlatformStatus = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/platform-settings`);
      const { customer_app_enabled, maintenance_mode, maintenance_message } = response.data;
      
      setIsAppEnabled(customer_app_enabled !== false);
      setIsMaintenanceMode(maintenance_mode === true);
      setMaintenanceMessage(maintenance_message || "");
      setLastChecked(new Date());
    } catch (error) {
      console.error("Error checking platform status:", error);
      // On error, assume app is enabled to prevent blocking users
      setIsAppEnabled(true);
      setIsMaintenanceMode(false);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Check on initial load
  useEffect(() => {
    checkPlatformStatus();
  }, [checkPlatformStatus]);

  // Re-check every 60 seconds
  useEffect(() => {
    const interval = setInterval(checkPlatformStatus, 60000);
    return () => clearInterval(interval);
  }, [checkPlatformStatus]);

  const value = {
    isAppEnabled,
    isMaintenanceMode,
    maintenanceMessage,
    isLoading,
    lastChecked,
    checkPlatformStatus, // Expose for manual re-check on navigation
    isAppDisabled: !isAppEnabled || isMaintenanceMode
  };

  return (
    <PlatformStatusContext.Provider value={value}>
      {children}
    </PlatformStatusContext.Provider>
  );
};

export default PlatformStatusContext;
